import ChatMessageContentType from "../../enum/chat/ChatMessageContentType";
import ChatMessageRole from "../../enum/chat/ChatMessageRole";
import { ChatBriefContent, ChatRecordCardContent } from "../../types/chat/ChatContent";
import {
  AssistantChatMessage,
  AssistantChatMessageBase,
  ChatContextEntity,
} from "../../types/chat/ChatMessage";
import ChatSuggestion from "../../types/chat/ChatSuggestion";
import { FieldAgentChatMessage, FieldAgentChatResponse } from "../../types/chat/FieldAgentChat";

import { CONTEXT_ENTITY_TYPES, isRecord } from "./chatWireGuards";
import parseCallCard from "./parseCallCard";
import parseProposals from "./parseProposals";
import parseRecordList from "./parseRecordList";

export interface ParsedChatResponse {
  conversationId: number;
  messages: AssistantChatMessage[];
  suggestions: ChatSuggestion[];
  // Collected rather than logged: this module is shared and cannot reach web-app's
  // loggingService, and parsing should not own a side effect anyway.
  warnings: string[];
}

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");

const parseContextEntity = (message: FieldAgentChatMessage): ChatContextEntity | undefined => {
  const { contextEntityId, contextEntityName, contextEntityType } = message;
  if (typeof contextEntityId !== "number" || typeof contextEntityType !== "string") {
    return undefined;
  }
  const entityType = CONTEXT_ENTITY_TYPES.find((candidate) => candidate === contextEntityType);
  if (!entityType) {
    return undefined;
  }
  // BE trims the name and omits the key when it is blank, so this is belt and braces. It is here
  // because every other field this mapper reads is validated, and a mapper that trusts one field
  // while checking the rest is the asymmetry the next content type copies.
  const name = typeof contextEntityName === "string" ? contextEntityName.trim() : "";
  return name ? { id: contextEntityId, entityType, name } : { id: contextEntityId, entityType };
};

const parseJsonContent = (content: string): Record<string, unknown> | undefined => {
  try {
    const parsed: unknown = JSON.parse(content);
    return isRecord(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

const parseBrief = (payload: Record<string, unknown>): ChatBriefContent | undefined =>
  typeof payload.narrative === "string" && isStringArray(payload.bullets)
    ? { bullets: payload.bullets, narrative: payload.narrative }
    : undefined;

const parseRecordCard = (payload: Record<string, unknown>): ChatRecordCardContent | undefined =>
  typeof payload.summary === "string" ? { summary: payload.summary } : undefined;

const parseMessage = (message: FieldAgentChatMessage, warnings: string[]): AssistantChatMessage => {
  const base: AssistantChatMessageBase = {
    id: `s-${message.id}`,
    contextEntity: parseContextEntity(message),
    createdAt: message.createdAt,
    platformId: message.id,
    role: ChatMessageRole.ASSISTANT,
  };

  const unsupported = (reason: string): AssistantChatMessage => {
    warnings.push(reason);
    return {
      ...base,
      contentType: ChatMessageContentType.UNSUPPORTED,
      rawContentType: message.contentType,
    };
  };

  const malformed = () =>
    unsupported(`Malformed chat payload for contentType "${message.contentType}"`);

  switch (message.contentType) {
    case ChatMessageContentType.SYSTEM_NOTICE:
    case ChatMessageContentType.TEXT:
      return { ...base, contentType: message.contentType, text: message.content };

    case ChatMessageContentType.BRIEF: {
      const payload = parseJsonContent(message.content);
      const brief = payload && parseBrief(payload);
      return brief ? { ...base, brief, contentType: ChatMessageContentType.BRIEF } : malformed();
    }

    case ChatMessageContentType.CALL_CARD: {
      const payload = parseJsonContent(message.content);
      const callCard = payload && parseCallCard(payload);
      return callCard
        ? { ...base, callCard, contentType: ChatMessageContentType.CALL_CARD }
        : malformed();
    }

    case ChatMessageContentType.RECORD_CARD: {
      const payload = parseJsonContent(message.content);
      const card = payload && parseRecordCard(payload);
      return card
        ? { ...base, card, contentType: ChatMessageContentType.RECORD_CARD }
        : malformed();
    }

    case ChatMessageContentType.RECORD_LIST: {
      const payload = parseJsonContent(message.content);
      const list = payload && parseRecordList(payload, warnings);
      return list
        ? { ...base, contentType: ChatMessageContentType.RECORD_LIST, list }
        : malformed();
    }

    case ChatMessageContentType.ACTION_PROPOSAL:
    case ChatMessageContentType.DEBRIEF: {
      const proposals = parseProposals(message.proposals, warnings);
      return proposals ? { ...base, contentType: message.contentType, proposals } : malformed();
    }

    default:
      return unsupported(`Unsupported chat contentType "${message.contentType}"`);
  }
};

// Never throws and never drops a message: a reply we cannot render still has to appear in
// the transcript, or the rep sees the agent say nothing at all.
const parseChatResponse = (response: FieldAgentChatResponse): ParsedChatResponse => {
  const warnings: string[] = [];
  return {
    conversationId: response.conversationId,
    messages: response.messages.map((message) => parseMessage(message, warnings)),
    // suggestions is `?:` on the wire type — the backend omits it entirely rather than sending
    // [] — so absent and empty are the same value, not a fallback for a malformed field.
    suggestions: response.suggestions ?? [],
    warnings,
  };
};

export default parseChatResponse;
